'use strict';
const _ = require('underscore'),
	observable = require('@mindmup/mapjs-core/src/util/observable'),
	toggleInArray = require('./toggle-in-array'),
	sortedSubIdeas = require('@mindmup/mapjs-core/src/content/sorted-sub-ideas'),
	traverse = require('@mindmup/mapjs-core/src/content/traverse'),
	URLHelper = require('@mindmup/mapjs-core/src/util/url-helper'),
	sanitiseMapTitle = require('./sanitise-map-title'),
	FONT_INCREMENT = 0.25,
	FONT_MULTIPLIER_BOUNDS = {
		max: 10,
		min: 1
	},
	BORDER_WIDTH_BOUNDS = {
		max: 10,
		min: 1
	},
	CORNER_RADIUS_BOUNDS = require('./corner-radius-bounds');
module.exports = function MapEditorModel({mapModel, nodeViewModel, maxBatchSize = 2000}) {
	if (!mapModel || !nodeViewModel) {
		throw new Error('invalid-args');
	}
	let selectedLine, isAddLinkMode = false;
	const dispatcher = 	observable(this),
		hasChildren = subIdea => subIdea && subIdea.ideas && Object.keys(subIdea.ideas).length,
		editLineLabel = (line) => {
			if (!line) {
				return false;
			}
			if (!mapModel.getEditingEnabled()) {
				return false;
			}
			if (!mapModel.getInputEnabled()) {
				return false;
			}
			dispatcher.dispatchEvent('lineLabelEditRequested', line);
		},
		updateLinkStyle = function (line, prop, value) {
			const idea = mapModel.getIdea(),
				merged = Object.assign({}, idea.getLinkAttr(line.ideaIdFrom, line.ideaIdTo, 'style'));
			merged[prop] = value;
			return idea.updateLinkAttr(line.ideaIdFrom, line.ideaIdTo, 'style', merged);
		},
		canActOnLine = function (line) {
			if (!mapModel.getEditingEnabled() || !mapModel.getInputEnabled()) {
				return false;
			}
			if (!line || !line.type) {
				return false;
			}
			return true;
		},
		canStyleIdea = function (idea) {
			return idea && (!idea.attr || !idea.attr.group);
		},
		sendContext = () => {
			const activatedIdeas = mapModel.getActivatedNodeIds().map(activatedId => mapModel.findIdeaById(activatedId)).filter(t => t),
				context = {},
				fontMultipliers = activatedIdeas.map(idea => {
					return (idea && idea.attr && idea && idea.attr.style && idea.attr.style.fontMultiplier) || 1;
				}),
				nodeViews = activatedIdeas.map(idea => nodeViewModel.getNodeViewForIdea(idea)).filter(t => t),
				nodeViewsWithStylableBorders = nodeViews.filter(v => v.theme?.borderType === 'surround'),
				borderWidths = nodeViewsWithStylableBorders.map(v => v?.theme?.lineWidth || 1),
				hasCornerRadiusStyled = !!activatedIdeas.find(v => v.attr?.style?.cornerRadius),
				maxFontMultiplier = Math.max.apply(this, fontMultipliers),
				minFontMultiplier = Math.min.apply(this, fontMultipliers),
				minBorderWidth =  Math.max.apply(this, borderWidths),
				maxBorderWidth =  Math.max.apply(this, borderWidths),
				cornerRadiuses = nodeViewsWithStylableBorders.map(v => v?.theme?.cornerRadius).filter(t => Number.isFinite(t)),
				minCornerRadius =  Math.min.apply(this, cornerRadiuses),
				maxCornerRadius =  Math.max.apply(this, cornerRadiuses),
				allowedToggleAttributesForNodes =  nodeViews.map(nodeView => nodeView.theme?.nodeEditingContext?.allowedToggleAttributes || []),
				canStyleBorders = !!(nodeViewsWithStylableBorders.length),
				childCounts = activatedIdeas.map(idea => idea.ideas && Object.keys(idea.ideas).length),
				minChildCount = childCounts.length && childCounts.reduce((a, c) => Math.min(a, c || 0)),
				allowedToggleAttributes = Array.isArray(allowedToggleAttributesForNodes) && allowedToggleAttributesForNodes.length && allowedToggleAttributesForNodes.reduce((a, c) => a.filter(t => c.includes(t)));

			context.canStyle = !!activatedIdeas.find(canStyleIdea);
			context.canIncreaseFont = context.canStyle && minFontMultiplier < FONT_MULTIPLIER_BOUNDS.max;
			context.canDecreaseFont = context.canStyle && maxFontMultiplier > FONT_MULTIPLIER_BOUNDS.min;
			context.canResetFont = context.canStyle && (maxFontMultiplier > 1 || minFontMultiplier < 1);
			context.addLinkMode = isAddLinkMode;
			context.canToggleCondensed = minChildCount > 0 && Array.isArray(allowedToggleAttributes) && allowedToggleAttributes.includes('condensed');

			context.canStyleBorders = canStyleBorders;
			context.canIncreaseBorderWidth =  canStyleBorders && context.canStyle && minBorderWidth < BORDER_WIDTH_BOUNDS.max;
			context.canDecreaseBorderWidth = canStyleBorders && context.canStyle && maxBorderWidth > BORDER_WIDTH_BOUNDS.min;
			context.canResetBorderWidth = canStyleBorders && context.canStyle && (minBorderWidth > 1 || maxBorderWidth < 1);

			context.canIncreaseCornerRadius =  canStyleBorders && context.canStyle && (!cornerRadiuses.length || minCornerRadius < CORNER_RADIUS_BOUNDS.max);
			context.canDecreaseCornerRadius = canStyleBorders && context.canStyle && (!cornerRadiuses.length || maxCornerRadius > CORNER_RADIUS_BOUNDS.min);
			context.canResetCornerRadius = canStyleBorders && context.canStyle && hasCornerRadiusStyled;
			dispatcher.dispatchEvent('context', context);
		},
		getCurrentStyleForNodeId = (id, style, fallback) => {
			const idea = mapModel.findIdeaById(id),
				currentAttr = idea && idea.attr;
			return (currentAttr && currentAttr.style && currentAttr.style[style]) || fallback || false;
		},
		incrementFontMultiplier = increment => {
			if (!mapModel.isEditingEnabled() || !mapModel.isEditingEnabled()) {
				return false;
			}
			const idea = mapModel.getIdea();
			mapModel.applyToActivated((id) => {
				const currentStyle = getCurrentStyleForNodeId(id, 'fontMultiplier', 1),
					current = parseFloat(currentStyle),
					newMultiple = current + increment;
				if (newMultiple < FONT_MULTIPLIER_BOUNDS.min || Math.abs(newMultiple - 1) < 0.01) {
					idea.mergeAttrProperty(id, 'style', 'fontMultiplier', false);
				} else if (!(newMultiple > FONT_MULTIPLIER_BOUNDS.max || newMultiple < FONT_MULTIPLIER_BOUNDS.min)) {
					idea.mergeAttrProperty(id, 'style', 'fontMultiplier', newMultiple);
				}

			});
		},
		updateLineProperty = (attr, value, line) => {
			let result = false;
			line = line || selectedLine;
			if (!canActOnLine(line)) {
				return false;
			}
			if (line.type === 'connector') {
				result = mapModel.getIdea().mergeAttrProperty(line.to, 'parentConnector', attr, value);
			} else if (line.type === 'link') {
				result = updateLinkStyle(line, attr, value);
			}
			if (result) {
				dispatcher.dispatchEvent('lineUpdated');
			}
			return result;
		},
		removeLine = (line) => {
			line = line || selectedLine;
			if (!canActOnLine(line)) {
				return false;
			}
			if (line.type === 'link') {
				mapModel.removeLink('mouse', line.ideaIdFrom, line.ideaIdTo);
			} else if (line.type === 'connector') {
				mapModel.selectNode(line.to);
				mapModel.makeSelectedNodeRoot();
			} else {
				throw 'invalid-args';
			}
		},
		getSelectedLine = () => {
			return selectedLine;
		},
		increaseNodeFontMultiplier = () => {
			incrementFontMultiplier(FONT_INCREMENT);
		},
		decreaseNodeFontMultiplier = function () {
			incrementFontMultiplier(-1 * FONT_INCREMENT);
		},
		nodeTextAlignLeft = () => {
			mapModel.updateStyle('mouse', 'textAlign', 'left');
		},
		nodeTextAlignRight = () => {
			mapModel.updateStyle('mouse', 'textAlign', 'right');
		},
		nodeTextAlignCenter = () => {
			mapModel.updateStyle('mouse', 'textAlign', 'center');
		},
		resetNodeFontMultiplier = () => {
			mapModel.updateStyle('mouse', 'fontMultiplier', false);
		},
		transitiveCollapse = () => {
			let collapseAtLevel = 1;
			const id = mapModel.getSelectedNodeId(),
				contentAggregate = mapModel.getIdea(),
				selectedIdea = contentAggregate.findSubIdeaById(id),
				recursivePass = function (idea, level) {
					if (idea.getAttr('collapsed')) {
						return;
					}
					if (!hasChildren(idea)) {
						return;
					}
					sortedSubIdeas(idea).forEach(subIdea => recursivePass(subIdea, level + 1));
					if (level > collapseAtLevel) {
						collapseAtLevel = level;
					}
				},
				collapser = function (idea, level) {
					if (idea.getAttr('collapsed')) {
						return;
					}
					if (hasChildren(idea) && level >= collapseAtLevel) {
						contentAggregate.updateAttr(idea.id, 'collapsed', true);
					}
				};
			recursivePass(selectedIdea, 1);
			return contentAggregate.batch(() => {
				traverse(selectedIdea, collapser, true, 1);
			});

		},
		resetPositioning = function () {
			const contentAggregate = mapModel.getIdea(),
				id = mapModel.getSelectedNodeId(),
				selectedIdea = contentAggregate.findSubIdeaById(id),
				resetter = (idea) => {
					contentAggregate.updateAttr(idea.id, 'position', false);
				};
			return contentAggregate.batch(() => {
				traverse(selectedIdea, resetter, true, 1);
			});
		},
		transitiveUncollapse = () => {
			const toCollapse = [],
				toUncollapse = [],
				id = mapModel.getSelectedNodeId(),
				contentAggregate = mapModel.getIdea(),
				selectedIdea = contentAggregate.findSubIdeaById(id),
				recursivePass = function (idea) {
					if (!idea.getAttr('collapsed')) {
						sortedSubIdeas(idea).forEach(recursivePass);
					} else {
						toUncollapse.push(idea.id);
						sortedSubIdeas(idea).forEach(subIdea => {
							if (hasChildren(subIdea)) {
								toCollapse.push(subIdea.id);
							}
						});
					}
				};


			if (!selectedIdea) {
				return false;
			}
			recursivePass(selectedIdea);
			return contentAggregate.batch(() => {
				toCollapse.forEach(sid => contentAggregate.updateAttr(sid, 'collapsed', true));
				toUncollapse.forEach(sid => contentAggregate.updateAttr(sid, 'collapsed', false));
			});
		},
		toggleAddLinkMode = () => {
			if (!mapModel.getEditingEnabled() || !mapModel.getInputEnabled()) {
				return false;
			}
			isAddLinkMode = !isAddLinkMode;
			sendContext();
		},
		toggleNodeStyleName = (styleName) => {
			if (!mapModel.getEditingEnabled() || !mapModel.getInputEnabled() || !styleName || typeof styleName !== 'string') {
				return false;
			}
			const attrName = 'styleNames',
				id = mapModel.getSelectedNodeId(),
				contentAggregate = mapModel.getIdea(),
				selectedIdea = contentAggregate.findSubIdeaById(id),
				currentValue = selectedIdea.getAttr(attrName);

			contentAggregate.updateAttr(id, attrName, toggleInArray(currentValue, styleName));
		},
		editSelectedNodeStyle = () => {
			const id = mapModel.getSelectedNodeId(),
				selectedIdea = mapModel.findIdeaById(id);
			if (!canStyleIdea(selectedIdea)) {
				return;
			}
			dispatcher.dispatchEvent('editNodeStyle', selectedIdea);
		},
		updateNodeStyles = (styleProperties) => {
			if (!mapModel.getEditingEnabled() || !mapModel.getInputEnabled() || !styleProperties || Object.keys(styleProperties).length === 0) {
				return false;
			}
			const contentAggregate = mapModel.getIdea();
			mapModel.applyToActivated((id) => {
				const idea = mapModel.findIdeaById(id);
				if (!canStyleIdea(idea)) {
					return;
				}
				Object.keys(styleProperties).forEach((prop) => {
					const value = styleProperties[prop],
						currentStyle = mapModel.getStyleForId(id, prop);
					if ((value || currentStyle) && !_.isEqual(currentStyle, value)) {
						contentAggregate.mergeAttrProperty(id, 'style', prop, value);
					}
				});
			});
		},
		resetLine = (line) => {
			line = line || selectedLine;
			const idea = mapModel.getIdea();
			if (!canActOnLine(line)) {
				return false;
			}
			if (!line || !['connector', 'link'].includes(line.type)) {
				return false;
			}
			if (line.type !== 'connector') {
				const result = idea.resetLink(line.ideaIdFrom, line.ideaIdTo);
				if (result) {
					dispatcher.dispatchEvent('lineUpdated');
				}
			} else {
				const currentProps = mapModel.getCurrentLineAttr(line),
					autocolor = currentProps && currentProps.themeAutoColor,
					newValue = autocolor ? {themeAutoColor: autocolor, color: autocolor} : false,
					result = idea.updateAttr(line.to, 'parentConnector', newValue);
				if (result) {
					dispatcher.dispatchEvent('lineUpdated');
				}
				return result;
			}
		},
		getSelectedLineAttr = () => {
			if (!canActOnLine(selectedLine)) {
				return false;
			}
			return mapModel.getCurrentLineAttr(selectedLine);
		},
		toggleCondensed = () => {
			if (!mapModel.getEditingEnabled()) {
				return false;
			}
			const idea = mapModel.getIdea();
			mapModel.applyToActivated(id => {
				const currentCondensed = !! idea.getAttrById(id, 'condensed');
				idea.updateAttr(id, 'condensed', ! currentCondensed);
			});
		},
		replaceSubmapWithLink = (subIdeaId, url) => {
			if (!subIdeaId || !url) {
				throw new Error('invalid-args');
			}
			const contentAggregate = mapModel.getIdea(),
				selectedIdea = contentAggregate.findSubIdeaById(subIdeaId),
				childIds = Object.values(selectedIdea.ideas || {}).map(i => i.id),
				oldTitle = selectedIdea.title || '',
				components = [url, URLHelper.stripLink(oldTitle)].filter(x => x);
			return contentAggregate.batch(() => {
				childIds.forEach(childId => contentAggregate.removeSubIdea(childId));
				contentAggregate.updateTitle(subIdeaId, components.join(' '));
			});
		},
		getSuggestedSubmapName = (selectedId) => {
			if (!selectedId) {
				throw new Error('invalid args');
			}
			const contentAggregate = mapModel.getIdea(),
				selectedIdea = contentAggregate.findSubIdeaById(selectedId),
				oldTitle = selectedIdea.title || '';
			return sanitiseMapTitle(oldTitle.trim() || 'Untitled map');
		},
		getSelectedId = () => mapModel.getSelectedNodeId(),
		getActivatedNodeIdsWithLayouts = () => {
			const activatedNodeIds = mapModel.getActivatedNodeIds(),
				layout = mapModel.getCurrentLayout();
			return activatedNodeIds.map(nodeId => ({nodeId, layout: layout.nodes[nodeId]}));
		},
		getAllColors = ({maxColors} = {}) => {

			const colors = new Set(),
				layout = mapModel.getCurrentLayout(),
				enoughColors = () => maxColors && (maxColors <= colors.size),
				addColor = color => {
					if (!color || enoughColors()) {
						return;
					}
					colors.add(color);
				},
				nodeIds = (layout?.nodes && Object.keys(layout?.nodes)) || [];
			for (const nodeId of nodeIds) {
				if (enoughColors()) {
					break;
				}
				const nodeView = nodeViewModel.getNodeViewForIdeaId(nodeId);
				addColor(nodeView?.theme?.backgroundColor);
				addColor(nodeView?.theme?.lineColor);
				addColor(nodeView?.theme?.text?.color);
			}
			return Array.from(colors);
		},
		collapseAll = () => {
			const contentAggregate = mapModel.getIdea(),
				toCollapseIds = [],
				sendNextBatch = function (startIndex) {
					if (startIndex >= toCollapseIds.length) {
						return;
					}
					const next = toCollapseIds.slice(startIndex, startIndex + maxBatchSize);
					contentAggregate.updateAttrBulk(next, 'collapsed', true);
					sendNextBatch(startIndex + maxBatchSize);
				};
			contentAggregate.traverse((idea) => {
				if (!idea.getAttr('collapsed') && hasChildren(idea) && (idea.id !== contentAggregate.id)) {
					toCollapseIds.push(idea.id);
				}
			});
			return sendNextBatch(0);
		},
		removeAllStyling = () => {
			const contentAggregate = mapModel.getIdea(),
				toClearIds = [],
				sendNextBatch = function (startIndex) {
					if (startIndex >= toClearIds.length) {
						return;
					}
					const next = toClearIds.slice(startIndex, startIndex + maxBatchSize);
					contentAggregate.batch(() => {
						if (startIndex === 0) {
							contentAggregate.updateAttr(contentAggregate.id, 'themeOverrides', false);
						}
						contentAggregate.updateAttrBulk(next, 'style', false);
					});

					sendNextBatch(startIndex + maxBatchSize);
				};
			contentAggregate.traverse((idea) => {
				if (idea.getAttr('style')) {
					toClearIds.push(idea.id);
				}
			});
			return sendNextBatch(0);

		};

	Object.assign(this, {
		editLineLabel: (line) => editLineLabel(line || selectedLine),
		setInputEnabled: mapModel.setInputEnabled,
		isEditingEnabled: mapModel.isEditingEnabled,
		updateLineProperty, removeLine, getSelectedLine, increaseNodeFontMultiplier, decreaseNodeFontMultiplier, nodeTextAlignLeft, nodeTextAlignRight, nodeTextAlignCenter, resetNodeFontMultiplier, transitiveCollapse, resetPositioning, transitiveUncollapse, toggleAddLinkMode, toggleNodeStyleName, canStyleIdea, editSelectedNodeStyle, updateNodeStyles, FONT_INCREMENT, FONT_MULTIPLIER_BOUNDS, BORDER_WIDTH_BOUNDS, CORNER_RADIUS_BOUNDS, resetLine, getSelectedLineAttr, toggleCondensed,
		replaceSubmapWithLink, getSuggestedSubmapName,
		getSelectedId, getActivatedNodeIdsWithLayouts, getAllColors,
		collapseAll, removeAllStyling
	});

	mapModel.addEventListener('connectorSelected', (connector) => {
		// TODO: move this to map model, it should get the whole connector from the layout and dispatch 'lineClicked'
		if (!connector) {
			return false;
		}
		selectedLine = connector;
		dispatcher.dispatchEvent('lineSelected', selectedLine);
	});

	mapModel.addEventListener('linkSelected', (link) => {
		selectedLine = link;
		dispatcher.dispatchEvent('lineSelected', selectedLine);
	});

	mapModel.addEventListener('lineLabelClicked', editLineLabel);

	mapModel.addEventListener('nodeClicked', (id) => {
		if (isAddLinkMode) {
			mapModel.toggleLink('mouse', id);
			dispatcher.toggleAddLinkMode();
		} else {
			mapModel.selectNode(id);
		}
	});


	mapModel.addEventListener('layoutChangeComplete', sendContext);
	mapModel.addEventListener('activatedNodesChanged', sendContext);



};
